import {
  createFilterFormStore,
  usePersistedFilterFormik,
} from "../../../util/usePersistedFilterFormik";
import { useEffect } from "react";
import {
  convertToFilter,
  DEFAULT_LIST_QUERY_PARAMS,
  makeObservable,
  parseFilterFromLocationSearch,
  serializeFilterToQueryParams,
} from "../../../util";
import { createSearchResultNavigationStore } from "../../../util/searchResultNavigation";
import { removeSearchSynopsisTerm } from "../../../util/search/searchSynopsis";

// observable store
const contactFilterStore = makeObservable({});
const contactNavigationStore =
  createSearchResultNavigationStore(contactFilterStore);
const CONTACT_PAGINATION_QUERY_PARAM = "p";
const DEFAULT_CONTACT_PAGINATION = { limit: 50, offset: 0 };
const RESERVED_CONTACT_QUERY_PARAMS = [
  ...DEFAULT_LIST_QUERY_PARAMS,
  CONTACT_PAGINATION_QUERY_PARAM,
];

const parseContactPaginationParam = (rawPaginationValue = "") => {
  const [rawOffset, rawLimit] = String(rawPaginationValue).split(",");
  const parsedOffset = Number(rawOffset);
  const parsedLimit = Number(rawLimit);

  if (
    Number.isInteger(parsedOffset) &&
    parsedOffset >= 0 &&
    Number.isInteger(parsedLimit) &&
    parsedLimit > 0
  ) {
    return { limit: parsedLimit, offset: parsedOffset };
  }

  return DEFAULT_CONTACT_PAGINATION;
};

const filterFormStore = createFilterFormStore(
  "contacts",
  contactFilterStore,
  contactNavigationStore,
  { searchLivesInUrl: true },
);

const useContactFilters = () => {
  const formik = usePersistedFilterFormik(
    {
      initialValues: {
        address: { operator: "eq" },
        countryId: { operator: "in" },
        county: { operator: "in" },
        email: { operator: "like" },
        exhUserId: { operator: "in" },
        id: { operator: "eq" },
        limit: 50,
        name: { operator: "like" },
        offset: 0,
        salesUser2Id: { operator: "in" },
        salesUserId: { operator: "in" },
        sort1: { operator: "ASC" },
        sort2: { operator: "ASC" },
        sort3: { operator: "ASC" },
        tab: "contact_details",
        town: { operator: "eq" },
        typeId: { operator: "in" },
        _contactArtist: { artistId: { operator: "in" } },
        _contactAttribute: { typeId: { operator: "in" } },
        _contactFocus: { focusId: { operator: "in" } },
        _contactMailingList: { mailingListId: { operator: "in" } },
        _invoice: {
          _item: {
            _instance: {
              _edition: {
                _artwork: {
                  _artist: { id: { operator: "in" } },
                  categoryId: { operator: "in" },
                },
              },
            },
          },
          analysisId: { operator: "in" },
        },
        _offer: { holdId: { operator: "gt" } },
      },
    },
    filterFormStore,
  );

  const filterKeys = ["contactCategory", "tab"];

  const skipKeys = ["limit", "offset"];

  useEffect(() => {
    // convert formik values to filter and update store
    contactFilterStore.set(
      convertToFilter({ values: formik.values, filterKeys, skipKeys }),
    );
  }, [formik.values]);

  return {
    formik,
  };
};

const getContactSearchFromLocationSearch = locationSearch => {
  return parseFilterFromLocationSearch({
    locationSearch,
    reservedKeys: RESERVED_CONTACT_QUERY_PARAMS,
  });
};

const serializeContactSearchToQueryParams = ({
  currentParams = new URLSearchParams(),
  searchInput,
} = {}) => {
  const src = searchInput ?? {};
  const { limit: _omitLimit, offset: _omitOffset, ...filterOnly } = src;
  return serializeFilterToQueryParams({
    currentParams,
    searchInput: filterOnly,
    reservedKeys: RESERVED_CONTACT_QUERY_PARAMS,
  });
};

const getContactPaginationFromLocationSearch = locationSearch => {
  const searchParams = new URLSearchParams(locationSearch);
  const paginationParam = searchParams.get(CONTACT_PAGINATION_QUERY_PARAM);

  if (!paginationParam) {
    return DEFAULT_CONTACT_PAGINATION;
  }

  return parseContactPaginationParam(paginationParam);
};

// Everything outside the reserved keys is search criteria, so keeping only the
// reserved keys clears the search; paging restarts at the first page.
const removeContactSearchFromLocationSearch = locationSearch => {
  const currentParams = new URLSearchParams(locationSearch);
  const { limit } = getContactPaginationFromLocationSearch(locationSearch);
  const reservedEntries = RESERVED_CONTACT_QUERY_PARAMS.filter(
    key => key !== CONTACT_PAGINATION_QUERY_PARAM && currentParams.get(key),
  ).map(key => [key, currentParams.get(key)]);
  return new URLSearchParams([
    ...reservedEntries,
    [CONTACT_PAGINATION_QUERY_PARAM, `0,${limit}`],
  ]).toString();
};

// Takes one synopsis term (or one of its values) out of the URL search;
// paging restarts at the first page.
const removeContactSearchTermFromLocationSearch = (locationSearch, removal) => {
  const currentParams = new URLSearchParams(locationSearch);
  const { limit } = getContactPaginationFromLocationSearch(locationSearch);
  currentParams.set(CONTACT_PAGINATION_QUERY_PARAM, `0,${limit}`);
  return serializeContactSearchToQueryParams({
    currentParams,
    searchInput: removeSearchSynopsisTerm(
      getContactSearchFromLocationSearch(locationSearch) || {},
      removal,
    ),
  });
};

// The Client Info tab derives `_contactCategory.category` from the
// `contactCategory` dropdown, so both must go or the tab restores it.
const LINKED_CONTACT_SEARCH_PATHS = {
  "_contactCategory.category": ["contactCategory"],
};

const getContactSearchFormRemovals = removal => [
  removal,
  ...(LINKED_CONTACT_SEARCH_PATHS[removal.path] || []).map(path => ({ path })),
];

// The My Contacts tab adds its own sales user filter, which is the tab rather
// than part of the user's search, so it can't be shown or removed as a term.
const getContactSearchSynopsisTerms = ({
  contactSearch,
  searchSynopsisTerms = [],
}) =>
  contactSearch?.salesUserId
    ? searchSynopsisTerms
    : searchSynopsisTerms.filter(term => term.path !== "salesUserId");

export {
  CONTACT_PAGINATION_QUERY_PARAM,
  getContactSearchFormRemovals,
  getContactSearchSynopsisTerms,
  removeContactSearchFromLocationSearch,
  removeContactSearchTermFromLocationSearch,
  serializeContactSearchToQueryParams,
  contactFilterStore,
  contactNavigationStore,
  getContactPaginationFromLocationSearch,
  getContactSearchFromLocationSearch,
  useContactFilters,
};
